import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { Profile } from './business-objects/profile-form-group';
import { AuthService } from './services/auth/auth.service';
import { PackageService } from './services/package/package.service';
import { AsyncPipe } from '@angular/common';
import { MatToolbar } from '@angular/material/toolbar';
import {
  FlexColumnComponent,
  FlexRowComponent,
  GrowDirective,
} from '@apexdesigner/flex-layout';
import { AvatarComponent } from '@components/avatar/avatar.component';
import { MarketingLayoutComponent } from '@components/marketing-layout/marketing-layout.component';
import { RibbonComponent } from '@components/ribbon/ribbon.component';
import { AdElDirective } from './directives/ad-el/ad-el.directive';

/**
 * App
 *
 * Root application component. Switches between the marketing layout
 * for unauthenticated users and the app layout for authenticated users.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: { adComp: '' },
  imports: [
    AdElDirective,
    AsyncPipe,
    AvatarComponent,
    FlexColumnComponent,
    FlexRowComponent,
    GrowDirective,
    MarketingLayoutComponent,
    MatToolbar,
    RibbonComponent,
    RouterLink,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);

  packageService = inject(PackageService);

  private router = inject(Router);

  /** Avatar Menu Items - Custom links for the avatar dropdown */
  avatarMenuItems: Array<{ label: string; path: string; icon?: string }> = [];

  /** Authenticated Subscription */
  private authenticatedSub?: Subscription;

  /** Ensure Profile Subscription */
  private ensureProfileSub?: Subscription;

  /** Redirect authenticated users away from the landing page */ redirectAuthenticatedFromLanding(): void {
    this.authenticatedSub = this.authService.authenticated.subscribe(
      (isAuthenticated: boolean | undefined) => {
        if (isAuthenticated && window.location.pathname === '/') {
          this.router.navigate(['/home']);
        }
      },
    );
  }

  /** Ensure the current user has a profile */ ensureProfile(): void {
    this.ensureProfileSub = this.authService.authenticated.subscribe(
      async (isAuthenticated: boolean | undefined) => {
        if (isAuthenticated) {
          await Profile.currentUserProfile(null);
        }
      },
    );
  }

  /** Cleanup */ cleanup(): void {
    this.authenticatedSub?.unsubscribe();
    this.ensureProfileSub?.unsubscribe();
  }

  /** Build Avatar Menu */

  async buildAvatarMenu(): Promise<void> {
    const items: Array<{ label: string; path: string; icon?: string }> = [
      { label: 'Profile', path: '/profile', icon: 'person' },
      { label: 'Account', path: '/accounts', icon: 'manage_accounts' },
      { label: 'Registry Tokens', path: '/registry-tokens', icon: 'key' },
    ];
    const isAdmin = await this.authService.hasRole('Administrator');
    if (isAdmin) {
      items.push({
        label: 'Manage Roles',
        path: '/manage-roles',
        icon: 'admin_panel_settings',
      });
      items.push({
        label: 'Admin Tools',
        path: '/admin-tools',
        icon: 'build',
      });
      items.push({
        label: 'User Activity',
        path: '/user-activity',
        icon: 'insights',
      });
    }
    this.avatarMenuItems = items;
  }

  ngOnInit(): void {
    this.redirectAuthenticatedFromLanding();
    this.ensureProfile();
    this.buildAvatarMenu();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
